import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Landmark, Banknote, Wallet } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { formatCurrency, formatPhoneNumber } from '../../utils/formatters';
import {
  validateName,
  validatePhone,
  validateEmail,
  validateRequired,
} from '../../utils/validators';
import { calculateShipping, generateOrderNo } from '../../utils/constants';

const MESSAGE_TEMPLATES = [
  '삼가 고인의 명복을 빕니다.',
  '삼가 조의를 표합니다.',
  '고인의 명복을 빌며 유가족께 깊은 위로를 전합니다.',
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, totalPrice, clearCart, getLineKey } = useCart();
  const { showSuccess, showError } = useToast();

  const timerRef = useRef(null);

  // 빈 장바구니 체크
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  // setTimeout 클린업
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const shippingCost = calculateShipping(totalPrice);
  const finalPrice = totalPrice + shippingCost;

  const [formData, setFormData] = useState({
    // 주문자(보내는 분) 정보
    senderName: '',
    ordererPhone: '',
    ordererEmail: '',
    // 배송지 정보
    funeralHome: '',
    address: '',
    funeralRoom: '', // 빈소(호실)
    deceasedName: '',
    mournerName: '',
    mournerPhone: '',
    // 메시지 카드 / 리본 문구
    messageCard: '',
    ribbonLeft: '',
    ribbonRight: '',
    // 결제 정보
    paymentMethod: 'card',
    // 동의
    agreePrivacy: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let nextValue = type === 'checkbox' ? checked : value;

    // 전화 자동 하이픈
    if (name === 'ordererPhone' || name === 'mournerPhone') {
      nextValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({ ...prev, [name]: nextValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const applyMessageTemplate = (text) => {
    setFormData((prev) => ({ ...prev, messageCard: text }));
    if (errors.messageCard) {
      setErrors((prev) => ({ ...prev, messageCard: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    newErrors.senderName = validateName(formData.senderName);
    newErrors.ordererPhone = validatePhone(formData.ordererPhone);
    newErrors.ordererEmail = validateEmail(formData.ordererEmail);

    newErrors.funeralHome = validateRequired(formData.funeralHome, '장례식장명');
    newErrors.address = validateRequired(formData.address, '주소');
    newErrors.deceasedName = validateName(formData.deceasedName);
    newErrors.mournerName = validateName(formData.mournerName);
    newErrors.mournerPhone = validatePhone(formData.mournerPhone);

    if (!formData.agreePrivacy) {
      newErrors.agreePrivacy = '개인정보 수집 및 이용에 동의해주세요.';
    }

    const filteredErrors = Object.entries(newErrors).reduce((acc, [key, value]) => {
      if (value) acc[key] = value;
      return acc;
    }, {});

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('입력 정보를 확인해주세요.');
      return;
    }

    setIsSubmitting(true);

    // Mock 결제 처리 (2초 딜레이)
    timerRef.current = setTimeout(() => {
      // B3: 주문번호 형식 통일 (ORD-YYYYMMDD-NNN)
      const orderId = generateOrderNo();

      const orderInfo = {
        orderId,
        senderName: formData.senderName,
        ordererPhone: formData.ordererPhone,
        ordererEmail: formData.ordererEmail,
        funeralHome: formData.funeralHome,
        address: formData.address,
        funeralRoom: formData.funeralRoom,
        deceasedName: formData.deceasedName,
        mournerName: formData.mournerName,
        mournerPhone: formData.mournerPhone,
        messageCard: formData.messageCard,
        ribbon: {
          left: formData.ribbonLeft,
          right: formData.ribbonRight,
          sender: formData.senderName,
        },
        items: cartItems.map((it) => ({
          wreathId: it.id,
          wreathName: it.name,
          image: it.image,
          price: it.price,
          quantity: it.quantity,
          ribbon: it.ribbon || null,
        })),
        subtotal: totalPrice,
        shippingCost,
        totalAmount: finalPrice,
        paymentMethod: formData.paymentMethod,
        orderStatus: 'pending',
        orderedAt: new Date().toISOString(),
      };

      clearCart();
      showSuccess('결제가 완료되었습니다!');

      navigate('/order-complete', { state: { orderInfo } });
      setIsSubmitting(false);
    }, 2000);
  };

  const paymentMethods = [
    { value: 'card', label: '신용카드', icon: CreditCard },
    { value: 'transfer', label: '계좌이체', icon: Landmark },
    { value: 'cash', label: '무통장입금', icon: Banknote },
    { value: 'kakao', label: '카카오페이', icon: Wallet },
  ];

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-2">주문/결제</h1>
          <p className="text-accent-600">배송 정보와 결제 수단을 입력해주세요</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* 주문자(보내는 분) 정보 */}
              <div className="bg-white rounded-lg border border-accent-200 p-6">
                <h2 className="text-xl font-bold text-primary mb-4">보내는 분 정보</h2>
                <div className="space-y-4">
                  <Input
                    label="이름 (리본/메시지에 표기)"
                    name="senderName"
                    value={formData.senderName}
                    onChange={handleChange}
                    placeholder="홍길동 / ○○회사 임직원 일동"
                    required
                    error={errors.senderName}
                  />
                  <Input
                    label="전화번호"
                    name="ordererPhone"
                    type="tel"
                    value={formData.ordererPhone}
                    onChange={handleChange}
                    placeholder="010-1234-5678"
                    required
                    error={errors.ordererPhone}
                  />
                  <Input
                    label="이메일"
                    name="ordererEmail"
                    type="email"
                    value={formData.ordererEmail}
                    onChange={handleChange}
                    placeholder="example@email.com"
                    required
                    error={errors.ordererEmail}
                  />
                </div>
              </div>

              {/* 배송지 정보 */}
              <div className="bg-white rounded-lg border border-accent-200 p-6">
                <h2 className="text-xl font-bold text-primary mb-4">배송지 정보</h2>
                <div className="space-y-4">
                  <Input
                    label="장례식장명"
                    name="funeralHome"
                    value={formData.funeralHome}
                    onChange={handleChange}
                    placeholder="○○장례식장"
                    required
                    error={errors.funeralHome}
                  />
                  <Input
                    label="주소"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="서울특별시 강남구 테헤란로 123"
                    required
                    error={errors.address}
                  />
                  <Input
                    label="빈소 (호실)"
                    name="funeralRoom"
                    value={formData.funeralRoom}
                    onChange={handleChange}
                    placeholder="예: 3호실 (선택사항)"
                  />
                  <Input
                    label="고인명"
                    name="deceasedName"
                    value={formData.deceasedName}
                    onChange={handleChange}
                    placeholder="故 홍길동"
                    required
                    error={errors.deceasedName}
                  />
                  <Input
                    label="상주명"
                    name="mournerName"
                    value={formData.mournerName}
                    onChange={handleChange}
                    placeholder="홍길동"
                    required
                    error={errors.mournerName}
                  />
                  <Input
                    label="상주 연락처"
                    name="mournerPhone"
                    type="tel"
                    value={formData.mournerPhone}
                    onChange={handleChange}
                    placeholder="010-1234-5678"
                    required
                    error={errors.mournerPhone}
                  />
                </div>
              </div>

              {/* 근조 리본 / 메시지 카드 */}
              <div className="bg-white rounded-lg border border-accent-200 p-6">
                <h2 className="text-xl font-bold text-primary mb-1">근조 리본 / 메시지 카드</h2>
                <p className="text-xs text-accent-600 mb-4">
                  화환에 부착할 리본 문구와 함께 보낼 메시지 카드를 입력하세요. (선택)
                </p>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="왼쪽 리본 문구"
                      name="ribbonLeft"
                      value={formData.ribbonLeft}
                      onChange={handleChange}
                      placeholder="謹弔 (근조)"
                    />
                    <Input
                      label="오른쪽 리본 문구"
                      name="ribbonRight"
                      value={formData.ribbonRight}
                      onChange={handleChange}
                      placeholder="삼가 고인의 명복을 빕니다"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="messageCard"
                      className="block text-sm font-medium text-primary mb-1"
                    >
                      메시지 카드
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {MESSAGE_TEMPLATES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => applyMessageTemplate(t)}
                          className="px-2.5 py-1 text-xs rounded-full border border-accent-300 text-accent-700 hover:border-primary hover:text-primary transition-colors"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    <textarea
                      id="messageCard"
                      name="messageCard"
                      value={formData.messageCard}
                      onChange={handleChange}
                      rows={3}
                      maxLength={200}
                      placeholder="삼가 고인의 명복을 빕니다."
                      className="w-full px-4 py-2 rounded-lg border border-accent-300 bg-white text-primary placeholder-accent-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                    <p className="mt-1 text-xs text-accent-500 text-right">
                      {formData.messageCard.length}/200
                    </p>
                  </div>
                </div>
              </div>

              {/* 결제 수단 */}
              <div className="bg-white rounded-lg border border-accent-200 p-6">
                <h2 className="text-xl font-bold text-primary mb-4">결제 수단</h2>
                <div className="space-y-3">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    const active = formData.paymentMethod === method.value;
                    return (
                      <label
                        key={method.value}
                        className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          active
                            ? 'border-primary bg-primary-50'
                            : 'border-accent-200 hover:border-accent-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.value}
                          checked={active}
                          onChange={handleChange}
                          className="w-5 h-5 text-primary"
                        />
                        <Icon size={20} className={active ? 'text-primary' : 'text-accent-600'} />
                        <span className="font-medium text-primary">{method.label}</span>
                      </label>
                    );
                  })}
                </div>
                {formData.paymentMethod === 'cash' && (
                  <div className="mt-3 p-3 bg-accent-50 rounded-lg border border-accent-200 text-xs text-accent-700">
                    입금 계좌: 국민은행 123-456-7890 (예금주: ○○화환). 주문 후 24시간 내 입금해주세요.
                  </div>
                )}
              </div>

              {/* 개인정보 동의 */}
              <div className="bg-white rounded-lg border border-accent-200 p-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreePrivacy"
                    checked={formData.agreePrivacy}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-primary">
                      개인정보 수집 및 이용에 동의합니다 <span className="text-red-600">*</span>
                    </span>
                    <p className="text-sm text-accent-600 mt-1">
                      주문 및 배송을 위해 필요한 최소한의 개인정보를 수집합니다.
                    </p>
                  </div>
                </label>
                {errors.agreePrivacy && (
                  <p className="mt-2 text-sm text-red-600">{errors.agreePrivacy}</p>
                )}
              </div>
            </div>

            {/* 주문 요약 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-accent-200 p-6 sticky top-4">
                <h2 className="text-xl font-bold text-primary mb-4">주문 요약</h2>

                <div className="mb-4 space-y-3 max-h-60 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div
                      key={getLineKey(item)}
                      className="flex items-center gap-3 pb-3 border-b border-accent-200"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/images/wreaths/placeholder.svg';
                        }}
                        className="w-16 h-16 rounded-md object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-primary truncate">{item.name}</p>
                        <p className="text-xs text-accent-600">
                          {item.quantity}개 × {formatCurrency(item.price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-accent-700">
                    <span>상품 금액</span>
                    <span className="font-medium">{formatCurrency(totalPrice)}</span>
                  </div>
                  <div className="flex items-center justify-between text-accent-700">
                    <span>배송비</span>
                    <span className="font-medium">
                      {shippingCost === 0 ? (
                        <span className="text-primary font-bold">무료</span>
                      ) : (
                        formatCurrency(shippingCost)
                      )}
                    </span>
                  </div>

                  <div className="border-t border-accent-200 pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-primary">최종 결제 금액</span>
                      <span className="text-2xl font-bold text-primary">
                        {formatCurrency(finalPrice)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '결제 처리 중...' : '결제하기'}
                </Button>

                <div className="mt-4 p-3 bg-accent-50 rounded-lg border border-accent-200">
                  <p className="text-xs text-accent-700">
                    결제 버튼 클릭 시 개인정보 수집 및 이용에 동의한 것으로 간주됩니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default CheckoutPage;
